import styles from '@ui/components/Voting/components/Slider/Slider.module.scss';
import Bubble from '@ui/components/Bubble/Bubble';
import Heading from '@ui/components/Heading/Heading';
import Button from '@ui/components/Button/Button';
import InputSlider from '@ui/components/InputSlider/InputSlider';
import { VisuallyHidden } from '@ariakit/react';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

const Slider = ({
  axisLabels,
  id,
  maxVal = 100,
  minVal = 0,
  onSubmitValue,
  question,
  results = null,
  sliderHandler = 'dot',
  stepVal = 1,
  unit = null,
}) => {
  const [sliderValue, setSliderValue] = useState(results?.answer || minVal);
  const [isConfirmed, setIsConfirmed] = useState(!!results?.average || false);

  const handleChange = value => {
    setSliderValue(Number(value));
  };

  const handleConfirm = () => {
    if (onSubmitValue) {
      onSubmitValue('slider', sliderValue);
    }
  };

  useEffect(() => {
    if (results) {
      setIsConfirmed(true);
      return;
    }
    setIsConfirmed(false);
    setSliderValue(minVal);
  }, [results]);

  // get the position of the bubble relative to max and min values
  const leftPosition = useMemo(() => {
    const average = Number(results?.average) || 0;
    const range = maxVal - minVal;
    return ((average - minVal) / range) * 100;
  }, [results]);

  const resultText = useMemo(
    () =>
      `Die durchschnittliche Bewertung liegt bei ${results?.average}${
        unit ? ` ${unit}` : ''
      }`,
    [results],
  );

  const ariaAttributes = useMemo(
    () => ({
      ariaLabelledby: `question-${id}`,
      ariaValuetext: unit
        ? `Sie haben ${sliderValue} ${unit} ausgewählt`
        : undefined,
    }),
    [id, sliderValue, unit],
  );

  return (
    <>
      <Heading
        align="center"
        as="div"
        className={styles.question}
        id={`question-${id}`}
        size="h4"
      >
        {question}
      </Heading>
      <div className={styles.rangeContainer}>
        {isConfirmed && (
          <div
            aria-hidden
            className={styles.bubbleWrapper}
            style={{ left: `${leftPosition}%` }}
          >
            <Bubble text="Durchschnittliche Bewertung" />
          </div>
        )}
        <InputSlider
          axisLabels={axisLabels || []}
          disabled={isConfirmed}
          hasProgression
          max={maxVal}
          min={minVal}
          onSlideChange={handleChange}
          step={stepVal}
          thumb={sliderHandler}
          thumbSize="md"
          value={sliderValue}
          {...ariaAttributes}
        />
      </div>
      {/* Own element for screen reader users that reports the average rating  */}
      {isConfirmed && (
        <VisuallyHidden render={<p />} role="status">
          {resultText}
        </VisuallyHidden>
      )}
      <Button
        className={styles.submitButton}
        disabled={isConfirmed}
        onClick={handleConfirm}
      >
        AUSWAHL BESTÄTIGEN
      </Button>
    </>
  );
};

Slider.propTypes = {
  axisLabels: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  maxVal: PropTypes.number,
  minVal: PropTypes.number,
  onSubmitValue: PropTypes.func,
  question: PropTypes.string.isRequired,
  results: PropTypes.shape({
    answer: PropTypes.number,
    average: PropTypes.number,
  }),
  sliderHandler: PropTypes.oneOf([
    'smiley',
    'thumbsUp',
    'thumbsDown',
    'indexDown',
    'dot',
  ]),
  stepVal: PropTypes.number,
  unit: PropTypes.string,
};

export default Slider;
